import { useState, useEffect, useCallback } from 'react';
import {
  fetchkeypickupServiceRequests,
  fetchkeypickupImportantDocuments,
  fetchSuiteUsers,
} from 'src/services/client';

export interface KeyPickupServiceRequest {
  id: string;
  [key: string]: any;
}

export interface KeyPickupImportantDocument {
  id: string;
  [key: string]: any;
}

export interface SuiteUser {
  id: string;
  firstname: string;
  lastname: string;
  [key: string]: any;
}

export interface UseKeyPickupDataReturn {
  serviceRequests: KeyPickupServiceRequest[];
  importantDocuments: KeyPickupImportantDocument[];
  suiteUsers: SuiteUser[];
  isLoading: boolean;
  isFetchingSuiteUsers: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  fetchSuiteUsersData: () => Promise<void>;
  resetState: () => void;
}

/* Custom hook for fetching key pickup service requests, important documents, and
 * suite users. Provides loading states, error handling, & refetch functionality */
export const useKeyPickupData = (suiteId: string): UseKeyPickupDataReturn => {
  const [serviceRequests, setServiceRequests] = useState<
    KeyPickupServiceRequest[]
  >([]);
  const [importantDocuments, setImportantDocuments] = useState<
    KeyPickupImportantDocument[]
  >([]);
  const [suiteUsers, setSuiteUsers] = useState<SuiteUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingSuiteUsers, setIsFetchingSuiteUsers] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuiteUsersData = useCallback(async () => {
    if (!suiteId) return;

    try {
      setIsFetchingSuiteUsers(true);

      const suiteUsersResponse: any = await fetchSuiteUsers(suiteId);
      let suiteUsersData: SuiteUser[] = [];

      if (suiteUsersResponse.status === 200 && suiteUsersResponse.ok) {
        const suiteUsersJson: any = await suiteUsersResponse.json();
        suiteUsersData = suiteUsersJson || [];
      }

      setSuiteUsers(suiteUsersData);
    } catch (error: any) {
      console.error('Error fetching suite users:', error);
      setSuiteUsers([]);
    } finally {
      setIsFetchingSuiteUsers(false);
    }
  }, [suiteId]);

  const fetchData = useCallback(async () => {
    if (!suiteId) return;

    try {
      setError(null);
      setIsLoading(true);

      const serviceRequestsResponse: any =
        await fetchkeypickupServiceRequests(suiteId);
      const importantDocumentsResponse: any =
        await fetchkeypickupImportantDocuments(suiteId);

      let serviceRequestsData: KeyPickupServiceRequest[] = [];

      if (serviceRequestsResponse.status === 200) {
        const serviceRequestsJson = await serviceRequestsResponse.json();
        serviceRequestsData = serviceRequestsJson || [];
      }

      let importantDocumentsData: KeyPickupImportantDocument[] = [];

      if (importantDocumentsResponse.status === 200) {
        const importantDocumentsJson = await importantDocumentsResponse.json();
        importantDocumentsData = importantDocumentsJson || [];
      }

      setServiceRequests(serviceRequestsData);
      setImportantDocuments(importantDocumentsData);
    } catch (error: any) {
      console.error('Error fetching key pickup data:', error);
      setError(error.message || 'Failed to fetch key pickup data');
      setServiceRequests([]);
      setImportantDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [suiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetState = useCallback(() => {
    setServiceRequests([]);
    setImportantDocuments([]);
    setSuiteUsers([]);
    setIsLoading(false);
    setIsFetchingSuiteUsers(false);
    setError(null);
  }, []);

  return {
    serviceRequests,
    importantDocuments,
    suiteUsers,
    isLoading,
    isFetchingSuiteUsers,
    error,
    refetch: fetchData,
    fetchSuiteUsersData,
    resetState,
  };
};
